import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const INLINE_DATE_PANEL_CLASS =
  "w-full overflow-hidden rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] p-0 !shadow-none text-[var(--t-text)] t-glass";

const parseDateValue = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const toDateValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Full-width inline calendar panel (render below a 2-col row to span both columns). */
export function InlineDatePickerPanel({
  value,
  onChange,
  onClose,
  allowClear = false,
  className = "",
  "data-testid": testId,
}) {
  const selected = useMemo(() => parseDateValue(value), [value]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={`${INLINE_DATE_PANEL_CLASS} ${className}`.trim()} data-testid={testId}>
      <Calendar
        mode="single"
        selected={selected}
        onSelect={(date) => {
          if (!date) return;
          onChange(toDateValue(date));
          onClose?.();
        }}
        initialFocus
        className="w-full min-w-0 p-3 sm:p-4 [&_.rdp-month]:w-full [&_.rdp-table]:w-full"
      />
      {allowClear && selected ? (
        <div className="border-t border-[var(--t-border)] p-2">
          <button
            type="button"
            className="t-btn-ghost w-full min-h-[36px] px-3 py-2 text-xs"
            onClick={() => {
              onChange("");
              onClose?.();
            }}
            data-testid={`${testId}-clear`}
          >
            Clear date
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Inline calendar (milestone-style) — use inside sheets/modals where popovers fail. */
export function InlineDatePickerField({
  value,
  onChange,
  placeholder = "Select date",
  allowClear = false,
  triggerClassName = "",
  expanded: expandedProp,
  onExpandedChange,
  detachPanel = false,
  "data-testid": testId,
}) {
  const [expandedInternal, setExpandedInternal] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : expandedInternal;
  const setExpanded = useCallback(
    (next) => {
      if (isControlled) onExpandedChange?.(next);
      else setExpandedInternal(next);
    },
    [isControlled, onExpandedChange],
  );
  const selected = useMemo(() => parseDateValue(value), [value]);

  useEffect(() => {
    if (!expanded || detachPanel) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detachPanel, expanded, setExpanded]);

  return (
    <div className={detachPanel ? undefined : "w-full"}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`t-input flex w-full min-h-[45px] items-center justify-between gap-3 text-left ${
          selected ? "" : "text-[var(--t-meta)]"
        } ${triggerClassName}`}
        data-testid={testId}
        aria-expanded={expanded}
      >
        <span className="truncate">{selected ? format(selected, "MMM d, yyyy") : placeholder}</span>
        <CalendarDays size={15} strokeWidth={1.4} className="shrink-0 text-[var(--t-meta)]" />
      </button>
      {!detachPanel && expanded ? (
        <InlineDatePickerPanel
          value={value}
          onChange={onChange}
          onClose={() => setExpanded(false)}
          allowClear={allowClear}
          className="mt-2"
          data-testid={`${testId}-panel`}
        />
      ) : null}
    </div>
  );
}

export function DatePickerField({
  value,
  onChange,
  placeholder = "Select date",
  allowClear = false,
  required = false,
  triggerClassName = "",
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseDateValue(value), [value]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`t-input flex min-h-[45px] items-center justify-between gap-3 text-left ${selected ? "" : "text-[var(--t-meta)]"} ${triggerClassName}`}
            data-testid={testId}
          >
            <span>{selected ? format(selected, "MMM d, yyyy") : placeholder}</span>
            <CalendarDays size={15} strokeWidth={1.4} className="shrink-0 text-[var(--t-meta)]" />
          </button>
        </PopoverTrigger>
        {allowClear && selected && (
          <button
            type="button"
            className="absolute right-10 top-1/2 -translate-y-1/2 text-[var(--t-meta)] hover:text-[var(--t-text)]"
            onClick={() => onChange("")}
            aria-label="Clear date"
          >
            <X size={13} strokeWidth={1.4} />
          </button>
        )}
      </div>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="z-[200] w-auto rounded-[var(--t-event-pill-radius)] p-0 text-[var(--t-text)] t-glass"
      >
        <Calendar
          mode="single"
          selected={selected}
          required={required}
          onSelect={(date) => {
            if (!date && required) return;
            onChange(toDateValue(date));
            setOpen(false);
          }}
          initialFocus
          className="p-4"
        />
      </PopoverContent>
    </Popover>
  );
}
